import React, { createContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import {
  MainMenu,
  AppDrawerMenu,
  Logo,
  Auth,
  SearchAdvisors
} from '../@components';
import HeaderRow from './styled';

const drawerWidth = 240;
export const PrimaryHeaderContext = createContext<{
  trackMixpanelEvent: (
    _name: string,
    _props: any,
    _sendImmediately: boolean
  ) => void;
}>({ trackMixpanelEvent: null });

function PrimaryHeader({
  trackMixpanelEvent
}: {
  trackMixpanelEvent?: any;
}): JSX.Element {
  const [appDrawerMenuOpen, setAppDrawerMenuOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setAppDrawerMenuOpen(!appDrawerMenuOpen);
  };

  const handleDrawerClose = () => {
    setAppDrawerMenuOpen(false);
  };

  const primaryHeaderContext = useMemo(
    () => ({
      trackMixpanelEvent
    }),
    [trackMixpanelEvent]
  );

  return (
    <PrimaryHeaderContext.Provider value={primaryHeaderContext}>
      <HeaderRow>
        <Container className="header-container">
          <Grid container className="header-grid">
            <Grid
              item
              xs={3}
              md={2}
              lg={2}
              sx={{
                display: {
                  xs: 'inherit',
                  sm: 'inherit',
                  md: 'none',
                  lg: 'none',
                  xl: 'none'
                }
              }}>
              <IconButton
                className="ham-burger"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: 'white',
                  mr: 2,
                  display: { md: 'none', lg: 'none' }
                }}>
                <MenuIcon />
              </IconButton>
              <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="mailbox folders">
                {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                  variant="temporary"
                  anchor="left"
                  open={appDrawerMenuOpen}
                  onClose={handleDrawerToggle}
                  ModalProps={{
                    keepMounted: true // Better open performance on mobile.
                  }}
                  sx={{
                    display: { sm: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                      boxSizing: 'border-box',
                      width: drawerWidth
                    }
                  }}>
                  <Toolbar>
                    <IconButton onClick={handleDrawerClose}>
                      <CloseIcon />
                    </IconButton>
                  </Toolbar>
                  <Divider />
                  <AppDrawerMenu />
                </Drawer>
              </Box>
            </Grid>
            <Grid item xs={6} md={2} lg={2} justifyContent="left">
              <Logo />
            </Grid>
            <Grid
              item
              xs={3}
              md={6}
              lg={6}
              sx={{
                marginTop: {
                  md: '15px'
                },
                paddingRight: {
                  md: '11px'
                },
                textAlign: 'right'
              }}>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    md: 'inline-block',
                    lg: 'inline-block',
                    xl: 'inline-block'
                  },
                  paddingRight: {
                    md: '3px',
                    lg: '2px'
                  }
                }}>
                <MainMenu />
              </Box>
              <Box
                sx={{
                  display: 'inline-block',
                  float: { xs: 'right', sm: 'right', md: 'none', lg: 'none' },
                  width: { xs: '100px', md: 'auto' }
                }}>
                <SearchAdvisors />
                <Box
                  sx={{
                    display: {
                      xs: 'inline-block',
                      sm: 'inline-block',
                      md: 'none',
                      lg: 'none'
                    }
                  }}>
                  <Auth mobileView />
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              md={4}
              lg={4}
              sx={{
                display: {
                  xs: 'none',
                  md: 'block',
                  lg: 'block',
                  xl: 'block'
                },
                textAlign: 'right'
              }}>
              <Auth />
            </Grid>
          </Grid>
        </Container>
      </HeaderRow>
    </PrimaryHeaderContext.Provider>
  );
}

PrimaryHeader.defaultProps = {
  trackMixpanelEvent: null
};

export default PrimaryHeader;
